import { useEffect, useState, Fragment, ReactNode } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordSingular } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { Carousel } from '@zola/zola-ui/src/components/Carousel';
import getVendorIconV3 from '@zola/zola-ui/src/util/getVendorIconV3';

import cx from 'classnames';
import _uniq from 'lodash/uniq';
import LazyLoad from 'react-lazyload';

import FavoriteHeart from '~/assets/images/explore-vendors/vendor-blender-favorite.svg';
import CardLinkWrapper from '~/components/common/cards/components/CardLinkWrapper';
import CachedImage from '~/components/common/images/CachedImage';
import { VendorCardView } from '~/types/responseTypes';
import { CAROUSEL_SETTINGS } from '~/util/carouselSettings';
import { centsToDollars } from '~/util/priceConversion';
import { getCanonicalUrl } from '~/util/storefrontUrl';
import { formatAsCurrency } from '~/util/textUtils';
import { TrackingProductLocation } from '~/util/trackingHelper';

import styles from './vendorDetails.module.less';

export type VendorDetailsProps = {
  className?: string | null;
  vendor: VendorCardView;

  carouselPhotoIds?: string[];

  onClick?: (event: React.MouseEvent<HTMLElement>) => void;

  /** Where is this card being displayed?  Used for tracking favorite clicks */
  location: TrackingProductLocation;
  /** 1 based position of the VendorCard for tracking, relative to totalHits */
  position: number;

  /**
   * Optional flag that will turn off lazy loading for the card if set to false
   *
   * By default, lazyLoading is true.
   */
  lazyLoad?: boolean;

  // eslint-disable-next-line react/no-unused-prop-types
  costContributionCents?: number;
  showFavoriteConfirmation?: boolean;
};

const getDisplayData = (vendor: VendorCardView, costContributionCents?: number) => ({
  to:
    vendor.storefrontSlug &&
    vendor.taxonomyNode &&
    getCanonicalUrl(vendor.taxonomyNode.key as VendorTaxonomyKey, vendor.storefrontSlug),
  vendorType: getVendorWordSingular(vendor.taxonomyNode?.key),
  storefrontUuid: vendor.storefrontUuid,
  imageUuid: vendor.storefrontCoverId,
  priceRangeVisible: vendor.priceRangeVisible,
  displayVendorName: vendor.vendorName,
  displayLocation:
    vendor.city && vendor.stateProvince ? `${vendor.city}, ${vendor.stateProvince}` : null,
  displayPrice: getDisplayPrice(vendor.startingPriceCents, costContributionCents),
  // INSPO-4132 - Boost Vendor Responsiveness Experiment
  storefrontResponsiveStats: vendor.storefrontResponsiveStatsView,
  taxonomyNodeKey: vendor.taxonomyNode?.key,
});

const getDisplayPrice = (startingPriceCents: number | null, costContributionCents?: number) => {
  if ((startingPriceCents || 0) > 0) {
    if (costContributionCents) {
      return formatAsCurrency(centsToDollars(costContributionCents));
    }
    return formatAsCurrency(centsToDollars(startingPriceCents));
  }
  return null;
};

const VendorCard = (props: VendorDetailsProps): JSX.Element => {
  const {
    className,
    vendor,
    carouselPhotoIds = [],
    onClick,
    lazyLoad = true,
    showFavoriteConfirmation = false,
    costContributionCents,
  } = props;
  const displayData = getDisplayData(vendor, costContributionCents);
  const { to, taxonomyNodeKey, imageUuid, priceRangeVisible, displayVendorName, displayPrice } =
    displayData;

  const cardContainerClasses = cx(styles.cardContainer, className, styles.compact);
  const [carouselCards, setCarouselCards] = useState<ReactNode[]>([]);

  useEffect(() => {
    if (carouselPhotoIds.length) {
      // Make sure the cover photo is always included
      const photosWithCover = _uniq([imageUuid, ...carouselPhotoIds]).filter(Boolean) as string[];

      setCarouselCards(
        photosWithCover.slice(0, 5).map((photoId: string, idx: number) => {
          return (
            <div className={styles.carouselPhoto} id={`photo-${idx}`} key={photoId}>
              <CachedImage
                uuid={photoId}
                srcSizes={[
                  { width: 200, height: 150 },
                  { width: 250, height: 188 },
                  { width: 300, height: 225 },
                  { width: 400, height: 300 },
                  { width: 500, height: 375 },
                  { width: 600, height: 450 },
                ]}
                fit="crop"
                quality={60}
              />
            </div>
          );
        })
      );
    }
  }, [carouselPhotoIds, imageUuid]);

  const VendorIcon = getVendorIconV3(taxonomyNodeKey || 'all');

  const coverPhotoDisplay = (
    <div className={styles.carouselPhoto}>
      <div>
        {imageUuid ? (
          <CachedImage
            uuid={imageUuid}
            srcSizes={[
              { width: 200, height: 150 },
              { width: 250, height: 188 },
              { width: 300, height: 225 },
              { width: 400, height: 300 },
              { width: 500, height: 375 },
              { width: 600, height: 450 },
            ]}
            fit="crop"
            quality={60}
          />
        ) : (
          <div className={styles.icon}>
            <VendorIcon width={32} height={32} alt="" />
          </div>
        )}
      </div>
    </div>
  );

  const getCoverImage = () => {
    if (carouselCards.length > 1) {
      return (
        <Carousel
          content={carouselCards}
          // making afterChangeHook null to prevent product list viewed from firing on arrow click
          settings={CAROUSEL_SETTINGS}
          useV2ArrowStyles
          useSmallerV2Arrows
        />
      );
    }
    return coverPhotoDisplay;
  };

  const filterCarouselClicks = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (!target) return;
    const arrowClicked = target.className.indexOf('slick-arrow') >= 0;
    const dotClicked = target.className.indexOf('carousel-dot') >= 0;
    const heartClicked = target.className.indexOf('favorite-icon') >= 0;
    if (!arrowClicked && !dotClicked && !heartClicked && onClick) {
      onClick(event);
    }
  };

  return (
    <div className={cardContainerClasses}>
      <CardLinkWrapper
        className={cx(styles.vendorDetails, { [styles.noLink]: !to }, styles.compact, className)}
        to={to}
        onClick={filterCarouselClicks}
      >
        <div className={styles.cardBody}>
          <div className={cx(styles.carouselContainer)}>
            <Fragment>
              <div
                className={cx(
                  styles.favoriteConfirmationWrapper,
                  showFavoriteConfirmation && styles.show
                )}
              >
                <img src={FavoriteHeart} alt="Favorited" />
              </div>
              {lazyLoad ? (
                <LazyLoad height={225} offset={300} once>
                  {getCoverImage()}
                </LazyLoad>
              ) : (
                getCoverImage()
              )}
            </Fragment>
          </div>
        </div>
        <div className={cx(styles.body)}>
          <div className={cx(styles.vendorName, styles.compact)}>{displayVendorName}</div>
          {Boolean(priceRangeVisible && displayPrice) && (
            <div
              style={{ fontWeight: 'normal' }}
              className={cx(styles.pricing, styles.location, styles.compact)}
            >
              Starting price {displayPrice}
            </div>
          )}
        </div>
      </CardLinkWrapper>
    </div>
  );
};

export default VendorCard;
